import React, { useState, useCallback, useMemo } from 'react';
import './HotelCompare.css';

function HotelCompare({
  hotels,
  searchParams,
  onSelectForBooking,
  onBackToResults,
  loading,
}) {
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [commissionRate, setCommissionRate] = useState(10);
  const [advisorNotes, setAdvisorNotes] = useState('');

  const nights = useMemo(() => {
    if (searchParams.checkIn && searchParams.checkOut) {
      return Math.ceil(
        (new Date(searchParams.checkOut) - new Date(searchParams.checkIn)) /
          (1000 * 60 * 60 * 24)
      );
    }
    return 1;
  }, [searchParams.checkIn, searchParams.checkOut]);

  const options = useMemo(() => {
    return hotels.map((hotel, idx) => {
      const baseRate = hotel.lowestRate || 0;
      const totalCost = baseRate * nights;
      const commission = (totalCost * commissionRate) / 100;

      return {
        label: String.fromCharCode(65 + idx), // A, B, C, D
        hotel,
        baseRate,
        totalCost,
        commission,
      };
    });
  }, [hotels, nights, commissionRate]);

  const handleExportPDF = useCallback(() => {
    alert('PDF export coming soon');
  }, []);

  const handleSendToAdvisor = useCallback(() => {
    alert('Send to advisor feature coming soon');
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="hotel-compare">
      <div className="compare-header">
        <div className="compare-title">
          <h2>Comparative Quote</h2>
          <p className="compare-subtitle">
            {nights} night{nights !== 1 ? 's' : ''} •{' '}
            {formatDate(searchParams.checkIn)} to{' '}
            {formatDate(searchParams.checkOut)} •{' '}
            {searchParams.guests} guest{searchParams.guests !== 1 ? 's' : ''} •{' '}
            {searchParams.rooms} room{searchParams.rooms !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="button-secondary" onClick={onBackToResults}>
          ← Back to Results
        </button>
      </div>

      <div className="compare-controls">
        <div className="view-toggle">
          <label>View:</label>
          <div className="toggle-buttons">
            <button
              className={`toggle-button ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              className={`toggle-button ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
        </div>

        <div className="commission-calculator">
          <label htmlFor="commission">Commission %:</label>
          <div className="commission-input-group">
            <input
              id="commission"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={commissionRate}
              onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
            />
            <span className="commission-percent">%</span>
          </div>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <div className="options-cards">
          {options.map((option) => (
            <div key={option.label} className="option-card">
              <div className="option-label">{option.label}</div>

              <div className="option-content">
                <h3>{option.hotel.name}</h3>
                <div className="option-rating">
                  <div className="stars">
                    {renderStars(option.hotel.starRating)}
                  </div>
                  <span className="rating-value">
                    {option.hotel.starRating}/5
                  </span>
                </div>

                <div className="option-details">
                  <div className="detail-item">
                    <span className="detail-label">Address</span>
                    <span className="detail-value">
                      {option.hotel.address?.city || option.hotel.city}{option.hotel.address?.state || option.hotel.state ? `, ${option.hotel.address?.state || option.hotel.state}` : ''}
                    </span>
                  </div>

                  {option.hotel.lowestRate && (
                    <div className="detail-item">
                      <span className="detail-label">Room Type</span>
                      <span className="detail-value">
                        {option.hotel.rooms?.[0]?.type || 'Standard Room'}
                      </span>
                    </div>
                  )}

                  <div className="detail-item">
                    <span className="detail-label">Rate/Night</span>
                    <span className="detail-value highlight">
                      ${option.baseRate.toFixed(2)}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label">Total ({nights} nights)</span>
                    <span className="detail-value highlight-large">
                      ${option.totalCost.toFixed(2)}
                    </span>
                  </div>

                  {commissionRate > 0 && (
                    <div className="detail-item commission-item">
                      <span className="detail-label">Commission ({commissionRate}%)</span>
                      <span className="detail-value commission-value">
                        ${option.commission.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {option.hotel.rooms?.[0]?.cancellationPolicy && (
                    <div className="detail-item">
                      <span className="detail-label">Cancellation</span>
                      <span className="detail-value">
                        {option.hotel.rooms[0].cancellationPolicy}
                      </span>
                    </div>
                  )}

                  {option.hotel.amenities && option.hotel.amenities.length > 0 && (
                    <div className="detail-item amenities-item">
                      <span className="detail-label">Amenities</span>
                      <div className="amenities-list">
                        {option.hotel.amenities.slice(0, 3).map((amenity, idx) => (
                          <span key={idx} className="amenity-badge">
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                className="button-primary select-button"
                onClick={() => onSelectForBooking(option.hotel)}
                disabled={loading}
              >
                Select Option {option.label}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="options-table-wrapper">
          <table className="options-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Hotel</th>
                <th>Rating</th>
                <th>Address</th>
                <th>Rate/Night</th>
                <th>Total ({nights} nights)</th>
                {commissionRate > 0 && <th>Commission</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {options.map((option) => (
                <tr key={option.label}>
                  <td className="table-label">{option.label}</td>
                  <td className="table-name">
                    <div>{option.hotel.name}</div>
                    {option.hotel.chainName && (
                      <span className="chain-badge-small">
                        {option.hotel.chainName}
                      </span>
                    )}
                  </td>
                  <td className="table-rating">
                    <div className="stars-small">
                      {renderStars(option.hotel.starRating)}
                    </div>
                  </td>
                  <td className="table-address">
                    {option.hotel.address?.city || option.hotel.city}{option.hotel.address?.state || option.hotel.state ? `, ${option.hotel.address?.state || option.hotel.state}` : ''}
                  </td>
                  <td className="table-rate">${option.baseRate.toFixed(2)}</td>
                  <td className="table-total">
                    ${option.totalCost.toFixed(2)}
                  </td>
                  {commissionRate > 0 && (
                    <td className="table-commission">
                      ${option.commission.toFixed(2)}
                    </td>
                  )}
                  <td className="table-action">
                    <button
                      className="button-small"
                      onClick={() => onSelectForBooking(option.hotel)}
                      disabled={loading}
                    >
                      Select
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="compare-footer">
        <div className="advisor-notes-section">
          <label htmlFor="notes">Advisor Notes:</label>
          <textarea
            id="notes"
            className="notes-textarea"
            placeholder="Add notes about these hotels for your records..."
            value={advisorNotes}
            onChange={(e) => setAdvisorNotes(e.target.value)}
            rows="4"
          ></textarea>
        </div>

        <div className="action-buttons">
          <button
            className="button-secondary"
            onClick={handleSendToAdvisor}
            disabled
            title="Coming soon"
          >
            📤 Send to Advisor
          </button>
          <button
            className="button-secondary"
            onClick={handleExportPDF}
            disabled
            title="Coming soon"
          >
            📄 Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default HotelCompare;
